<template>
  <div>
    <Autocomplete
      :search="search ? search : defaultSearch"
      :get-result-value="getResultValue"
      :default-value="defaultInnerText"
      @update="onUpdate"
      :autoSelect="autoSelect"
    >
      <template
        #default="{
          rootProps,
          inputProps,
          inputListeners,
          resultListProps,
          resultListListeners,
          results,
          resultProps
        }"
      >
        <div v-bind="rootProps" class="field">
          <input
            v-if="!disabled"
            :id="id"
            role="textbox"
            :data-testid="`${id}-search`"
            :aria-label="`${name}-search`"
            type="text"
            v-bind="inputProps"
            v-on="inputListeners"
            class="autocomplete-input field__input"
            :class="invalid ? 'border-danger' : ''"
            ref="input"
            @blur="onBlur"
            @keyup="$emit('keypressed', $event.target.value)"
            @click="
              $refs.input.blur()
              $refs.input.focus()
            "
            :readonly="!autocomplete"
            :style="{ cursor: 'pointer' }"
            placeholder=" "
          />
          <input
            v-else
            :id="id"
            role="textbox"
            :aria-label="`${name}-search`"
            type="text"
            v-bind="inputProps"
            v-on="inputListeners"
            class="autocomplete-input field__input"
            :class="invalid ? 'border-danger' : ''"
            :disabled="true"
            :style="{ color: getNameByCode === '' ? '#cccccc' : 'black' }"
            :value="getNameByCode !== '' ? getNameByCode : translate('ppNotSpecified')"
          />
          <i
            v-if="!disabled"
            class="xxs input-icon icon-arrow-down"
            @click="$refs.input.focus()"
          ></i>
          <label
            v-if="responsiveLabelStyle"
            :for="id"
            class="field__labelResponsive"
            :class="invalid && innerValue ? 'text-danger' : ''"
            >{{ placeholder }}</label
          >
          <label
            v-if="!responsiveLabelStyle"
            :for="id"
            class="field__label"
            :class="invalid && innerValue ? 'text-danger' : ''"
            >{{ placeholder }}</label
          >
          <ul class="results" v-bind="resultListProps" v-on="resultListListeners">
            <li
              role="listitem"
              :data-testid="result.code"
              :aria-label="result.name"
              v-for="(result, index) in results"
              :key="resultProps[index].code"
              v-bind="resultProps[index]"
              class="autocomplete-result"
              @click="result.code !== innerValue ? onClick(result) : null"
            >
              {{ result.name }}
            </li>
          </ul>
        </div>
      </template>
    </Autocomplete>
    <ValidationProvider
      :vid="id"
      ref="validator"
      :rules="rules"
      v-slot="{ errors, failedRules, touched }"
    >
      <input
        :required="required"
        ref="hidden"
        role="textbox"
        :aria-label="name"
        v-model="innerValue"
        :name="name"
        class="d-none"
      />
      <div v-if="failedRules.required" class="field__error -mt-1 mb-3">
        {{ translate('rwThisIsRequired') }}
      </div>
      <span>{{ updateValidation(errors, touched) }}</span>
    </ValidationProvider>
  </div>
</template>

<script>
import Autocomplete from '@trevoreyre/autocomplete-vue'
import { ValidationProvider } from 'vee-validate'

export default {
  name: 'SelectField',
  components: {
    Autocomplete,
    ValidationProvider
  },
  data: () => ({
    innerValue: '',
    defaultInnerText: '',
    invalid: false,
    touched: false
  }),
  props: {
    id: {
      type: String,
      required: true
    },
    required: {
      type: Boolean,
      default: false
    },
    autoSelect: {
      type: Boolean,
      default: true
    },
    name: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      default: ' '
    },
    options: {
      type: Array,
      required: true
    },
    rules: {
      type: String,
      default: ''
    },
    value: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      defaultValue: false
    },
    autocomplete: {
      type: Boolean,
      defaultValue: false
    },
    search: {
      type: Function,
      defaultValue: null
    },
    responsiveLabelStyle: {
      type: Boolean,
      default: false
    },
    defaultInnerTextProp: {
      type: String,
      default: ''
    }
  },
  watch: {
    value(newValue) {
      this.updateValue(newValue)
    }
  },
  computed: {
    getNameByCode() {
      const found = this.options.filter(
        option => option.code.toLowerCase() === this.innerValue.toLowerCase()
      )
      if (found[0]) {
        return found[0].name
      } else {
        return ''
      }
    }
  },
  created() {
    this.updateValue(this.value)
  },
  mounted() {
    global.UtilsObserver.observerDOM({
      element: document.getElementById(this.id),
      ref: this.id,
      type: 'select-field'
    })
  },
  methods: {
    defaultSearch(input) {
      return this.autocomplete
        ? this.options.filter(option => option.name.toLowerCase().startsWith(input.toLowerCase()))
        : this.options
    },
    getResultValue(option) {
      return option.name
    },
    onUpdate(results, selectedIndex) {
      /*
      debounce(() => {
        if (selectedIndex > -1) {
          if (results[selectedIndex].code !== this.innerValue) {
            this.$emit('changed', results[selectedIndex].code)
          }
          this.innerValue = results[selectedIndex].code
        }
      }, 300, {
        leading: true,
        trailing: false
      })
      */
    },
    onClick(result) {
      this.innerValue = result.code
      this.$emit('changed', this.innerValue)
    },
    updateValue(value) {
      if (value !== '') {
        const found = this.options.filter(
          option => option.code?.toString().toLowerCase() === value?.toString().toLowerCase()
        )
        if (found.length > 0) {
          this.innerValue = found[0].code
          this.defaultInnerText = found[0].name
          this.$emit('update-inner-text', this.defaultInnerText)
        } else {
          this.innerValue = ' '
        }
      }
    },
    updateValidation(errors, touched) {
      this.invalid = !!errors.length
      this.touched = touched
    },
    onBlur(event) {
      const term = event.target.value
      const found = this.options.filter(option => option.name.toLowerCase() === term.toLowerCase())
      if (found.length > 0) {
        this.innerValue = found[0].code
        this.$refs.validator.setFlags({
          dirty: true,
          invalid: false,
          pending: false,
          pristine: false,
          required: true,
          touched: true,
          untouched: false,
          valid: true,
          validated: true
        })
      } else if (term === '') {
        this.$refs.validator.setFlags({
          changed: false,
          dirty: true,
          invalid: false,
          pending: false,
          pristine: false,
          required: true,
          touched: true,
          untouched: false,
          valid: true,
          validated: true
        })
      } else {
        this.innerValue = ' '
        this.$refs.validator.setFlags({
          changed: false,
          dirty: true,
          invalid: true,
          pending: false,
          pristine: false,
          required: true,
          touched: true,
          untouched: false,
          valid: false,
          validated: true
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$color-grey: #c2c3c5;

.results {
  max-height: 200px;
  overflow-y: auto;
}

.-mt-1 {
  margin-top: -1rem;
}

.input-icon {
  position: absolute !important;
  margin: 1rem !important;
  right: 0 !important;

  html[dir='rtl'] & {
    position: absolute !important;
    margin: 1rem !important;
    left: 0!important;
    right: unset!important;
  }
}

.field {
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 1rem 0;
  width: 100%;
  row-gap: 0.1rem;

  &__input {
    &[disabled] {
      border: 0;
      background-color: white;
    }
  }

  &__label {
    position: absolute;
    background: white;
    top: 1.85rem;
    left: 1rem;
    font-size: 1.4rem;
    transition: top 200ms ease-in, left 200ms ease-in, font-size 200ms ease-in;

    html[dir="rtl"] & {
      right: 1rem;
      left: unset;
      width: fit-content;
    }
  }
  &__labelResponsive {
    position: absolute;
    background: white;
    top: 1.85rem;
    left: 1rem;
    font-size: 1.4rem;
    transition: top 200ms ease-in, left 200ms ease-in, font-size 200ms ease-in;
    white-space: nowrap;
    max-width: 90%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__error {
    color: crimson;
    font-size: 1.1rem;
  }
}

.autocomplete-input {
  font-size: 1.4rem;
  padding: 0.75rem 1rem;
  border: solid 1px $color-grey;
  border-radius: 0.4rem;
  width: 100%;
  background-size: 0.8rem;
}

.autocomplete-result-list {
  background: white;
  border: solid 1px $color-grey;
  border-radius: 0.4rem;
}

.autocomplete-result {
  padding: 0.75rem 1rem;
  transition: background 100ms ease-in;
}

.autocomplete-result[aria-selected='true'],
.autocomplete-result:hover {
  background: $color-grey;
  transition: background 200ms ease-in;
}

.field__input:disabled ~ .field__label,
.field__input:focus ~ .field__label,
.field__input:not(:placeholder-shown).field__input:not(:focus) ~ .field__label {
  top: 0;
  font-size: 1.1rem;
}
.field__input:disabled ~ .field__labelResponsive,
.field__input:focus ~ .field__labelResponsive,
.field__input:not(:placeholder-shown).field__input:not(:focus) ~ .field__labelResponsive {
  top: 0;
  font-size: 1.1rem;
}

input:focus {
  outline: none;
}
</style>
